/**
 * Plataformas contratadas por cliente — o catálogo fechado.
 *
 * ESPELHA `backend/app/services/contracted_platforms.py` do RC2 AI Manager.
 * A autoridade do catálogo é lá; aqui é a mesma lista com os rótulos que o
 * gestor lê. Chave nova entra nos dois lugares ou o espelhamento devolve
 * 422 — de propósito: valor inválido nunca pode silenciar uma pendência.
 *
 * POR QUE ISTO DEIXOU DE SER TEXTO LIVRE (20/09/2026)
 * `clients.notes` era digitado à mão e três consumidores só entendiam as
 * palavras "meta" e "google":
 *
 *   1. a Virada de Mês (n8n) só cria AC quando casa um dos dois — sem
 *      casar, o cliente passa o mês inteiro SEM TAREFA NENHUMA, sem erro
 *      e sem aviso;
 *   2. o Briefing Diário só cobra tarefa com prazo, então o cliente some
 *      da rotina junto;
 *   3. a RC2 Platform (`domain/clientRegistration.ts`) marca "Plataformas"
 *      como campo essencial FALTANDO quando não reconhece o que está
 *      escrito — alarme falso em cadastro preenchido.
 *
 * Foi o que aconteceu com o Grupo THE, cadastrado em LinkedIn Ads. Uma
 * caixinha não é conveniência de interface: é o que impede um cliente de
 * existir fora de toda automação por causa de uma palavra digitada.
 *
 * O FORMATO CONTINUA SENDO TEXTO, E ISSO É DELIBERADO
 * O valor gravado em `notes` permanece legível — "Meta Ads + Google Ads ·
 * desde mar/2026". Nenhum consumidor precisou de migração: o n8n, a
 * Platform e `buildChecklist` continuam lendo por substring, agora sobre
 * um texto que ninguém digita errado. Coluna nova exigiria migration no
 * Supabase do CRM e deixaria os consumidores antigos lendo a coluna
 * velha — duas verdades, que é exatamente o que este arquivo evita.
 */

export const PLATAFORMAS_CONTRATADAS = [
  {
    value: "google_ads",
    label: "Google Ads",
    /** O motor do AI Manager coleta esta fonte. */
    collectable: true,
    /** Tem rotina própria no CRM — declarar gera checklist mensal. */
    rotina: true,
    /** Como reconhecer a plataforma num `notes` escrito antes disto. */
    match: /google/i,
    hint: "Coletado pelo motor. Contratar sem vincular a conta vira pendência na Central.",
  },
  {
    value: "meta_ads",
    label: "Meta Ads",
    collectable: true,
    rotina: true,
    match: /\bmeta\b|facebook/i,
    hint: "Coletado pelo motor. Contratar sem vincular a conta vira pendência na Central.",
  },
  {
    value: "linkedin_ads",
    label: "LinkedIn Ads",
    collectable: false,
    rotina: true,
    match: /linked\s*in/i,
    hint: "Sem coletor no motor — fica registrado e nunca vira pendência. Tem rotina própria no CRM.",
  },
  {
    value: "tiktok_ads",
    label: "TikTok Ads",
    collectable: false,
    /**
     * Sem template de checklist ainda. A caixinha DECLARA a plataforma
     * (é fato comercial e vale para o AI Manager), mas o formulário avisa
     * que ela não gera rotina — porque foi exatamente o silêncio nesse
     * ponto que deixou o Grupo THE sem tarefa.
     */
    rotina: false,
    match: /tik\s*tok/i,
    hint: "Sem coletor no motor e ainda sem rotina no CRM: declarar registra, mas não gera checklist.",
  },
  {
    value: "ga4",
    label: "GA4",
    collectable: true,
    rotina: false,
    match: /\bga4\b|google analytics/i,
    hint: "Não é mídia: é a medição. Entra aqui porque o motor a coleta e cobra o vínculo da propriedade.",
  },
];

const POR_VALOR = new Map(PLATAFORMAS_CONTRATADAS.map((p) => [p.value, p]));

export const plataforma = (value) => POR_VALOR.get(value) || null;

/** As que geram checklist. Usado pelo aviso do formulário. */
export const semRotina = (values) =>
  values.map(plataforma).filter((p) => p && !p.rotina);

/**
 * Lê um `notes` de qualquer época e devolve o que dá para reconhecer.
 *
 * `resto` é tudo que sobrou depois de tirar os rótulos — "desde mar/2026",
 * "cliente envia criativo", o que for. Ele é PRESERVADO na gravação: o
 * campo era livre e jogar fora o que alguém escreveu seria apagar
 * informação para caber num formato novo.
 */
export function parsePlataformas(notes = "") {
  const texto = String(notes || "");
  const values = PLATAFORMAS_CONTRATADAS.filter((p) => p.match.test(texto)).map((p) => p.value);

  let resto = texto;
  for (const p of PLATAFORMAS_CONTRATADAS) {
    // Remove o rótulo inteiro ("Google Ads") e também a forma solta
    // ("Google"), que é como metade da carteira foi cadastrada.
    resto = resto.replace(new RegExp(p.label, "gi"), " ");
    // `match` nasce sem /g (é usado com `.test`); sem o /g aqui o replace
    // tiraria só a primeira ocorrência e deixaria a segunda no resto.
    resto = resto.replace(new RegExp(p.match.source, "gi"), " ");
  }
  resto = resto
    .replace(/\bads\b/gi, " ")
    // A barra NÃO entra aqui: "desde mar/2026" é observação legítima e
    // virava "desde mar 2026" quando ela era tratada como separador.
    .replace(/[+·|,;]+/g, " ")
    .replace(/\s{2,}/g, " ")
    .replace(/^[\s\-–—]+|[\s\-–—]+$/g, "")
    .trim();

  return { values, resto };
}

/**
 * Monta o texto que vai para `clients.notes`.
 *
 * A ordem é a do catálogo, não a de clique: duas pessoas marcando as
 * mesmas caixas precisam produzir a mesma string, senão o campo muda
 * sozinho a cada edição e o histórico fica ilegível.
 */
export function formatPlataformas(values = [], resto = "") {
  const rotulos = PLATAFORMAS_CONTRATADAS.filter((p) => values.includes(p.value)).map((p) => p.label);
  const obs = String(resto || "").trim();
  if (!rotulos.length) return obs;
  return obs ? `${rotulos.join(" + ")} · ${obs}` : rotulos.join(" + ");
}
